/* eslint-disable */
import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card'
import { Badge } from '../../Components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../Components/ui/dialog'
import { Button } from '../../Components/ui/button'
import { Input } from '../../Components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../Components/ui/select'
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { useRecoilValueLoadable } from 'recoil'
import { FetchKycCustomers } from '../../store/AdminStore'
import KycPagination from '../../Components/Admin/KycPagination'

const KycUsers = () => {
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const { state, contents: kycUsers } = useRecoilValueLoadable(
    FetchKycCustomers({ page: currentPage, size: pageSize }),
  )

  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [kycStatusFilter, setKycStatusFilter] = useState('ALL')
  const [sortOrder, setSortOrder] = useState('none')

  // Function to flatten nested objects for modal
  const flattenObject = (obj, prefix = '') => {
    const result = []

    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}.${key}` : key

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        result.push(...flattenObject(value, newKey))
      } else {
        result.push({ key: newKey, value })
      }
    }

    return result
  }

  // Function to format key names for better readability
  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/\./g, ' → ')
  }

  // Function to format values
  const formatValue = (value) => {
    if (value === null) return <span className='text-muted-foreground'>null</span>
    if (value === '') return <span className='text-muted-foreground'>Empty</span>
    if (typeof value === 'boolean') {
      return <Badge variant={value ? 'default' : 'secondary'}>{value ? 'True' : 'False'}</Badge>
    }
    return String(value)
  }

  // Get KYC status badge variant
  const getKycStatusVariant = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'secondary'
      case 'FAILURE':
        return 'destructive'
      case 'PENDING':
        return 'secondary'
      case 'NEEDS_REVIEW':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  // Filtered and sorted data
  const filteredData = useMemo(() => {
    const filtered = kycUsers?.data?.filter((item) => {
      // Search filter
      const searchMatch =
        item.uccRequest.clientFullName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        item.clientCode?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        item.phoneNumber.includes(searchTerm)

      // KYC status filter
      const kycMatch = kycStatusFilter === 'ALL' || item.kycStatus === kycStatusFilter

      return searchMatch && kycMatch
    })

    // Sort by submission date
    if (sortOrder !== 'none') {
      filtered?.sort((a, b) => {
        const dateA = new Date(a.createdTimestamp).getTime()
        const dateB = new Date(b.createdTimestamp).getTime()
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
      })
    }

    return filtered
  }, [searchTerm, kycStatusFilter, sortOrder, kycUsers])

  // Get unique KYC statuses for filter dropdown
  const kycStatuses = Array.from(new Set(kycUsers?.data?.map((item) => item.kycStatus)))

  const toggleSort = () => {
    if (sortOrder === 'none') setSortOrder('asc')
    else if (sortOrder === 'asc') setSortOrder('desc')
    else setSortOrder('none')
  }

  const getSortIcon = () => {
    if (sortOrder === 'asc') return <ArrowUp className='h-4 w-4' />
    if (sortOrder === 'desc') return <ArrowDown className='h-4 w-4' />
    return <ArrowUpDown className='h-4 w-4' />
  }

  // Reset to first page when filters change
  const handleFilterChange = (filterType, value) => {
    setCurrentPage(0)
    if (filterType === 'search') setSearchTerm(value)
    if (filterType === 'kycStatus') setKycStatusFilter(value)
    if (filterType === 'pageSize') setPageSize(Number(value))
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <div className='w-full mx-auto p-4'>
      <Card>
        <CardHeader>
          <CardTitle className='text-black-500'>KYC Management</CardTitle>
          <CardDescription className='text-black-400'>
            Manage and view KYC requests with filtering and search capabilities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters Section */}
          <div className='flex flex-col sm:flex-row gap-4 mb-6'>
            {/* Search Input */}
            <div className='relative flex-1'>
              <Search className='absolute z-10 left-3 top-1/2 transform -translate-y-1/2 text-black-400 h-4 w-4' />
              <Input
                placeholder='Search by name or client code...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>

            {/* KYC Status Filter */}
            <Select value={kycStatusFilter} onValueChange={setKycStatusFilter}>
              <SelectTrigger className='w-full sm:w-48 border'>
                <SelectValue placeholder='Filter by KYC Status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ALL'>All Statuses</SelectItem>
                {kycStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort Button */}
            <Button
              variant='outline'
              onClick={toggleSort}
              className='flex items-center gap-2 bg-transparent'
            >
              {getSortIcon()}
              Sort by Date
            </Button>
          </div>

          {/* Results Summary and Page Size Selector */}
          <div className='flex justify-between items-center mb-4'>
            <div className='mb-4 text-sm text-muted-foreground'>
              Showing {filteredData?.length} of {kycUsers?.data?.length} requests
            </div>

            <div className='flex items-center gap-2'>
              <span className='text-sm text-muted-foreground'>Show:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => handleFilterChange('pageSize', value)}
              >
                <SelectTrigger className='w-20 h-8 border'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='5'>5</SelectItem>
                  <SelectItem value='10'>10</SelectItem>
                  <SelectItem value='25'>25</SelectItem>
                  <SelectItem value='50'>50</SelectItem>
                  <SelectItem value='100'>100</SelectItem>
                </SelectContent>
              </Select>
              <span className='text-sm text-muted-foreground'>per page</span>
            </div>
          </div>

          {/* Summary Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client Name</TableHead>
                <TableHead>Client Code</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>KYC Status</TableHead>
                <TableHead>UCC Status</TableHead>
                <TableHead>Submitted On</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state === 'loading' && (
                <TableRow>
                  <TableCell colSpan={7} className='text-center py-8 text-muted-foreground'>
                    Loading...
                  </TableCell>
                </TableRow>
              )}

              {state === 'hasValue' &&
                (filteredData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className='text-center py-8 text-muted-foreground'>
                      No requests found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className='font-medium'>
                        {item.uccRequest.clientFullName}
                      </TableCell>
                      <TableCell>{item.clientCode}</TableCell>
                      <TableCell>{item.phoneNumber}</TableCell>
                      <TableCell>
                        <Badge variant={getKycStatusVariant(item.kycStatus)}>
                          {item.kycStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.uccRegistered ? 'secondary' : 'destructive'}>
                          {item.uccRegistered ? 'Registered' : 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(item.createdTimestamp).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant='outline' size='sm'>
                              View Details
                            </Button>
                          </DialogTrigger>
                          <DialogContent
                            className='max-w-4xl max-h-[80vh] overflow-y-auto hide-scrollbar'
                            showclose={true}
                          >
                            <DialogHeader>
                              <DialogTitle>
                                UCC Request Details - {item.uccRequest.clientFullName}
                              </DialogTitle>
                              <DialogDescription>
                                Complete information for client code: {item.clientCode}
                              </DialogDescription>
                            </DialogHeader>
                            <div className='mt-4'>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className='w-1/3'>Field</TableHead>
                                    <TableHead>Value</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {flattenObject(item).map((field, index) => (
                                    <TableRow key={index}>
                                      <TableCell className='font-medium text-sm'>
                                        {formatKey(field.key)}
                                      </TableCell>
                                      <TableCell className='text-sm'>
                                        {formatValue(field.value)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))
                ))}
            </TableBody>
          </Table>

          <div className='mt-6'>
            <KycPagination
              currentPage={currentPage}
              totalPages={kycUsers?.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default KycUsers
